import { useMemo } from "react";
import { Box, Divider, List, Text } from "@mantine/core";
import type {
  ReportCorrectionNoticeFragmentConfig,
  ReportSupplementEntry,
} from "../../types/reportTemplate";
import { buildCorrectionDescriptions } from "./reportCorrectionContent";
import { LAB_TIME_ZONE } from "../../utils/labTime";

interface ReportCorrectionEntryProps {
  entry: ReportSupplementEntry;
  showDivider: boolean;
  fragment?: ReportCorrectionNoticeFragmentConfig;
}

function correctionCompletionDate(entry: ReportSupplementEntry): Date {
  const dateValue =
    entry.correctionEvidence?.completedAt || entry.createdAt || entry.signedAt;
  return new Date(dateValue);
}

function formatCorrectionTime(dateValue: Date): string {
  return dateValue.toLocaleTimeString("en-US", {
    timeZone: LAB_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatCorrectionDate(dateValue: Date): string {
  return dateValue.toLocaleDateString("en-US", {
    timeZone: LAB_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function ReportCorrectionEntry({
  entry,
  showDivider,
  fragment,
}: ReportCorrectionEntryProps) {
  const descriptions = useMemo(
    () =>
      fragment?.correctionDescriptions ?? buildCorrectionDescriptions(entry),
    [entry, fragment]
  );
  const actor = useMemo(
    () => entry.correctionEvidence?.actor,
    [entry.correctionEvidence?.actor]
  );
  const completedAt = useMemo(() => correctionCompletionDate(entry), [entry]);
  const correctionTime = useMemo(
    () => formatCorrectionTime(completedAt),
    [completedAt]
  );
  const correctionDate = useMemo(
    () => formatCorrectionDate(completedAt),
    [completedAt]
  );
  const actorInitials = useMemo(
    () =>
      actor?.initials ||
      initialsFromName(actor?.fullName || entry.signedBy || "Unknown"),
    [actor?.fullName, actor?.initials, entry.signedBy]
  );

  return (
    <Box
      data-report-correction-entry={entry.id}
      style={{ breakInside: "avoid", pageBreakInside: "avoid" }}
    >
      {showDivider && <Divider my="8pt" />}
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) auto",
          columnGap: "16pt",
          alignItems: "end",
        }}
      >
        {descriptions.length === 1 && !fragment?.correctionUsesList ? (
          <Text size="sm">{descriptions[0]}</Text>
        ) : (
          <List size="sm" spacing={2}>
            {descriptions.map((description) => (
              <List.Item key={description}>{description}</List.Item>
            ))}
          </List>
        )}
        {(fragment?.showAttribution ?? true) && (
          <Text
            data-report-correction-attribution="true"
            size="xs"
            fw={600}
            ta="right"
            style={{ whiteSpace: "nowrap" }}
          >
            {correctionTime} {correctionDate} {actorInitials}
          </Text>
        )}
      </Box>
    </Box>
  );
}
